import { Link } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { SPACE_DEFAULTS } from '@dove/shared';
import { PostCard } from '@/components/PostCard';
import {
  SpaceList,
  SpaceResults,
  SpaceSearchField,
  useSpaceSearch,
} from '@/components/space/SpaceSearch';
import { Failed, Loading, Note } from '@/components/states';
import { TopRow } from '@/components/TopRow';
import { Button } from '@/components/ui/button';
import { feedQueryOptions } from '@/lib/feed-query';
import { useJoinedSpaces, useSpaceRooms } from '@/lib/membership';

/**
 * The main feed (docs/1 § Surfaces).
 *
 * WHAT IS IN THIS COLUMN IS OPEN LETTERS, AND NOW IT IS NOTHING ELSE (docs/5 § D260803-1952,
 * § D260803-1954) — public, filed in a room, and anyone who can see one may comment on it.
 * `post` is the word the code and the schema keep; `PostCard` argues the split, and
 * nothing this screen renders says it.
 *
 * THE FEED OPENS ON ONE ROW AND THEN ON A LETTER (docs/5 § D260803-1933). That row is a
 * search field over ROOMS, and it is the only thing this screen has ever been
 * given above the column — the wordmark, the notifications bell and the theme
 * switcher were taken off it and moved to your own social card, and the composer
 * prompt that replaced them became the `CreateButton` in the middle of the tab
 * bar. The rule those changes established still holds and this row does not break
 * it: the top of the feed is not for saying which screen you are on, and this row
 * does not say it. It is a control, in the shape the Loft's row already uses.
 *
 * WHAT THE FIELD IS FOR IS THE ONLY REASON IT IS HERE. It finds rooms, including
 * rooms you are not in — a place rather than a person, which is what makes it
 * possible at all in a product with no people search (docs/5 § D260730-2028, and the
 * argument is in § D260803-1933 and in `SpaceSearch`). It never matches a handle.
 *
 * TAPPING IT REPLACES THE COLUMN, and it does so before a letter is typed
 * (docs/5 § D260805-1233): the panel opens on rooms and narrows to what you type from
 * there. Results are rows and letters are sheets, so the two states of this screen
 * cannot be mistaken for each other. The X in the field puts the feed back.
 *
 * WHAT IT OPENS ON IS THE WHOLE DIRECTORY NOW, a page at a time, ordered by how much
 * has been written in each room (docs/5 § D260805-1500). It was a handful of six in a
 * fixed order, and the paragraph here used to say that the handful being unranked was
 * the whole of why it was allowed to exist — that is the line § D260805-1500 reopened
 * and answered differently. Read it before narrowing this back down; the argument
 * turns on a room being a place and the figure counting letters rather than anybody's
 * engagement. See `useSpaceSearch`.
 *
 * NOTHING ON THIS SCREEN PROPOSES A PERSON (docs/5 § D260803-0022). There was a *who to
 * follow* module three letters in — three names, a line each on what they write
 * about, and a Follow button. It is gone, and the feed is now writing and nothing
 * else from the first pixel to the last. The reasoning is in the decision; the
 * part that belongs here is what the screen gained: it has one job, and a reading
 * surface interrupted to introduce strangers has two.
 *
 * TWO BUCKETS, AND ONLY ONE OF THEM IS RANKED (docs/5 D260801-1119). Letters from your
 * mutual follows arrive in time order. Everything else is the discovery slice,
 * scored on content affinity and freshness and never on how many people
 * engaged — and every discovered letter says why it is here, which is also how
 * you tell the two apart, so a stranger never arrives unannounced.
 *
 * Composition settles near FEED_MUTUAL_TARGET_SHARE as an account builds its
 * people, and is discovery-dominated before that. A new user's feed is mostly
 * strangers by construction.
 *
 * Absent on purpose:
 *   - **Every engagement count.** No likes, no comments, no letters sent on, for
 *     anyone including the author. A visible tally is a ranking of people that
 *     renders itself, and it is half of what manufactures an influencer.
 *   - **People search.** The field at the top searches ROOMS. There is no field
 *     anywhere in this product that takes a name, and a lookup over strangers is
 *     the browsing surface docs/5 D260730-2028 removed, rebuilt as a text box.
 *   - **A spaces switcher.** A room is a place you go (`/spaces/$slug`), not a
 *     mode this screen is in — a switcher would make the feed a filtered view of
 *     whichever room you last picked, which is a different product.
 *   - **Anything that browses people.** No deck, no cards, no swiping
 *     (docs/5 D260730-2028) — and now not the constrained suggestions module the docs
 *     allowed either, which was the single exception and is not built (§ D260803-0022).
 *     People are met in public, in the column below, by what they wrote.
 *   - **A "trending" or "popular" anything IN THIS COLUMN.** No letter is ranked by
 *     how many people engaged with it, and no person is ranked at all — which is the
 *     part of this that was always load-bearing. What is no longer true is the reason
 *     that used to be given for it, that no quantity in the system could sort such a
 *     surface: a ROOM can be sorted, by how much has been written in it, and the
 *     directory behind the field at the top is (docs/5 § D260805-1500). Nothing about
 *     the letters below is.
 *
 * THE STRAY IS GONE, AND WITH IT THE ONE THING IN THIS COLUMN THAT WAS NOT AN OPEN
 * LETTER (docs/5 § D260803-1954). A full `LetterSheet` used to sit after one named group — an
 * unaddressed letter a pigeon had carried to a stranger, with *Write back* and *Send
 * it on* on its bottom edge — held outside `MOCK_FEED` because a `FeedGroup` holds
 * `PostData[]` and a stray was a different type. That placement, the fixture and
 * `FoundStray` are all deleted, and the loop below is the whole of the unsearched
 * state. That is the shape to protect: anything that wants a slot in this column has
 * to be an open letter, or it has to argue again for a second kind of object in a
 * reading surface.
 */
export function FeedScreen() {
  /*
   * LOCAL, AND NOT IN THE URL. A search over rooms is a thing you are doing on this
   * screen for a moment, not a place — the place is the room, and it has its own
   * address (`/spaces/$slug`). Putting the query in the feed's search params would
   * also have made every keystroke a navigation, and the two review overrides that
   * live in the URL (`?theme=`, `?dir=`) are not this screen's to rewrite.
   *
   * The cost is that coming back from a room opens the feed rather than the results
   * you left, which is the ordinary behaviour of a back button and is one tap to
   * undo.
   *
   * THE STATE IS THE SEARCH'S OWN NOW (`useSpaceSearch`), not a string this screen
   * holds: the panel opens on a tap rather than on the first keystroke
   * (docs/5 § D260805-1233), which is a second piece of state, and the field and the panel
   * sit in different corners of this tree. What this screen still decides is the one
   * thing that is its own — that the panel stands in front of the column.
   */
  const search = useSpaceSearch();

  const feed = useInfiniteQuery(feedQueryOptions());
  const more = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = more.current;
    if (!target || !feed.hasNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !feed.isFetching) void feed.fetchNextPage();
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [feed.fetchNextPage, feed.hasNextPage, feed.isFetching]);

  const groups = feed.data?.pages.flatMap((page) => page.groups) ?? [];

  return (
    <div>
      {/* The one row this tab gets, in the shape every tab's row uses — 40px of
          control, no rule under it, not sticky (see `TopRow`). */}
      <TopRow>
        <SpaceSearchField search={search} />
      </TopRow>

      {search.open ? (
        <SpaceResults panel={search.panel} />
      ) : feed.isPending ? (
        <Loading />
      ) : feed.isError ? (
        <Failed onRetry={() => void feed.refetch()} />
      ) : groups.length === 0 ? (
        <EmptyFeed />
      ) : (
        <>
          {groups.map((group) => (
            <div key={group.id} className="feed-group">
              {group.posts.map((post, j) => (
                <PostCard
                  key={post.id}
                  post={post}
                  /* A comment directly under the letter it answers is drawn as one
                     sheet folded in two, and does not repeat who it is answering. */
                  connected={j < group.posts.length - 1}
                  continued={j > 0}
                  showReplyContext={j === 0}
                  /* The feed's comment control is the conversation entrance: readers
                     see what has already been said before deciding whether to add to
                     it. The thread keeps its own Comment control for writing. */
                  commentAction="open"
                />
              ))}
            </div>
          ))}
          <div ref={more} className="flex min-h-20 items-center justify-center px-4 py-5">
            {feed.isFetchingNextPage ? (
              <p className="text-xs text-muted-foreground" role="status">Loading more…</p>
            ) : feed.hasNextPage ? (
              <Button
                variant="ghost"
                size="sm"
                className="rounded-full text-muted-foreground"
                onClick={() => void feed.fetchNextPage()}
              >
                Load more
              </Button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * A FEED WITH NOTHING IN IT, WHICH IS THE FIRST USERS' NORMAL CASE and will be for
 * some time. It is the most important empty state in the product and the easiest one
 * to ruin.
 *
 * WHAT IT IS NOT: a tutorial. No "your feed is where letters from people you follow
 * appear", no three-step how-to, no explanation of the discovery slice, no
 * encouragement. docs/1 § Copy forbids all of it, and the reason bites hardest right
 * here — somebody looking at an empty screen is the most captive audience an app ever
 * gets, and that is exactly why it must not be spent on prose.
 *
 * ONE LINE AND TWO WAYS TO ACT, both of which genuinely fill this column: writing
 * something, and the rooms — a room's writing reaches your feed, so joining one is the
 * literal fix rather than a suggestion. The rooms are the same rows the search field
 * returns (`SpaceList`), so this is not a new surface, it is the surface that already
 * exists shown without a query.
 *
 * NO PEOPLE ARE PROPOSED. There was a *who to follow* module on this screen and it is
 * gone (docs/5 § D260803-0022); an empty feed is precisely where it would come back, and a list
 * of strangers offered to somebody with no feed is the browsing surface § D260730-2028 removed.
 * A room has no photograph and no interest in you, which is what makes it the thing
 * this screen may offer.
 */
function EmptyFeed() {
  const joined = useJoinedSpaces();

  /*
   * THE SAME PAGE THE SEARCH PANEL OPENS ON, which is why this reads the search
   * endpoint with no query rather than the directory (docs/5 § D260805-1500). Before
   * that decision the two lists came from different places — this one from
   * `GET /spaces`, the panel's from a slice assembled in `useSpaceSearch` — and a row
   * here would have carried no count while the identical row under the field carried
   * one. One query, one shape, one cache entry, and `SpaceList` cannot be handed a
   * room whose letters nobody counted.
   *
   * A HANDFUL, NOT THE DIRECTORY, AND THAT PART HAS NOT CHANGED. The seeded database
   * has over fifty rooms; all of them rendered here would turn the most important
   * empty state in the product into a scrolling index. The field at the top is where
   * browsing belongs now, and `SPACE_OFFER` is what an offer is.
   *
   * The rooms you are NOT in, because the ones you are in are already writing into
   * this column — offering them would be offering something that has been done. That
   * filter runs over the first page, so a reader who is in most of the busiest rooms
   * may be offered fewer than `SPACE_OFFER`; asking for a second page to top the list
   * back up would be paging an empty state.
   */
  const rooms = useSpaceRooms('');

  const toJoin = (rooms.data?.pages[0]?.spaces ?? [])
    .filter((s) => !joined.has(s.id))
    .slice(0, SPACE_DEFAULTS.SPACE_OFFER);

  return (
    <>
      <Note
        className="py-10"
        action={
          <Button asChild variant="outline" className="rounded-full">
            <Link to="/compose">Write</Link>
          </Button>
        }
      >
        Nothing here yet.
      </Note>

      {toJoin.length > 0 && <SpaceList spaces={toJoin} joined={joined} />}
    </>
  );
}
